<script setup>
import { ref } from 'vue';
import { VueFlow, Position } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

// Define nodes for the workflow - Clean organized layout
const nodes = ref([
  // A. Onboarding Section Header
  {
    id: 'a-header',
    type: 'default',
    position: { x: 40, y: 30 },
    data: { label: '🚀 A. ONBOARDING | (5-10 Days)' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '13px',
      padding: '12px 16px',
      borderRadius: '10px',
      width: '240px',
      textAlign: 'center',
      boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
      whiteSpace: 'pre-wrap'
    }
  },
  {
    id: 'a1',
    type: 'default',
    position: { x: 330, y: 30 },
    data: {
      label: '🏦 Open FCA Account | Foreign Currency Account with IBU (HDFC/ICICI/Kotak at GIFT City)'
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
      border: '2px solid #3b82f6',
      borderRadius: '10px',
      padding: '12px',
      width: '320px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#1e3a8a',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(59, 130, 246, 0.15)'
    }
  },
  {
    id: 'a2',
    type: 'default',
    position: { x: 700, y: 30 },
    data: {
      label: '✅ Complete KYC | Digital-first KYC (5-7 business days)'
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
      border: '2px solid #2563eb',
      borderRadius: '10px',
      padding: '12px',
      width: '320px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#1e40af',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(37, 99, 235, 0.15)'
    }
  },

  // B. Investment Process Section Header
  {
    id: 'b-header',
    type: 'default',
    position: { x: 40, y: 140 },
    data: { label: '💰 B. INVESTMENT PROCESS' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '13px',
      padding: '12px 16px',
      borderRadius: '10px',
      width: '240px',
      textAlign: 'center',
      boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)'
    }
  },
  {
    id: 'b1',
    type: 'default',
    position: { x: 330, y: 140 },
    data: {
      label: '📤 Fund Remittance | Transfer INR → GIFT City FCA (via LRS Form A2)'
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
      border: '2px solid #10b981',
      borderRadius: '10px',
      padding: '12px',
      width: '320px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#064e3b',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(16, 185, 129, 0.15)'
    }
  },
  {
    id: 'b2',
    type: 'default',
    position: { x: 700, y: 140 },
    data: {
      label: '💱 Currency Conversion | INR → USD at Bank Rates (Better than retail forex)'
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
      border: '2px solid #059669',
      borderRadius: '10px',
      padding: '12px',
      width: '320px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#065f46',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(5, 150, 105, 0.15)'
    }
  },
  {
    id: 'b3',
    type: 'default',
    position: { x: 350, y: 250 },
    data: {
      label: '📊 Investment Deployment\n\nInvest USD in: GIFT ETFs (NYSE, LSE) | UDRs (NSE IX) | 💼 GIFT Mutual Funds'
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
      border: '2px solid #10b981',
      borderRadius: '10px',
      padding: '12px',
      width: '360px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#064e3b',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(16, 185, 129, 0.15)',
      lineHeight: '1.5'
    }
  },

  // C. Redemption & Repatriation Section Header
  {
    id: 'c-header',
    type: 'default',
    position: { x: 40, y: 360 },
    data: { label: '🔄 C. REDEMPTION & REPATRIATION' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '13px',
      padding: '12px 16px',
      borderRadius: '10px',
      width: '240px',
      textAlign: 'center',
      boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)'
    }
  },
  {
    id: 'c1',
    type: 'default',
    position: { x: 330, y: 360 },
    data: {
      label: '🔓 Redeem Anytime | Liquidity: Flexible (No lock-in period)'
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
      border: '2px solid #8b5cf6',
      borderRadius: '10px',
      padding: '12px',
      width: '320px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#581c87',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(139, 92, 246, 0.15)'
    }
  },
  {
    id: 'c2',
    type: 'default',
    position: { x: 700, y: 345 },
    data: {
      label: '⭐ Hold in USD | KEY ADVANTAGE: Keep proceeds in USD indefinitely | No forced conversion!'
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '3px solid #f59e0b',
      borderRadius: '10px',
      padding: '12px',
      width: '320px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#78350f',
      fontWeight: 'bold',
      boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)'
    }
  },
  {
    id: 'c3',
    type: 'default',
    position: { x: 310, y: 480 },
    data: {
      label: '🇮🇳 Repatriate to India | Convert USD → INR\n\n✓ Inflow: $250k/year (LRS) | ✅ Outflow: UNLIMITED'
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
      border: '2px solid #7c3aed',
      borderRadius: '10px',
      padding: '12px',
      width: '420px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#5b21b6',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(124, 58, 237, 0.15)',
      lineHeight: '1.5'
    }
  }
]);

// Define edges (connections between nodes) - Creative flow with arrows
const edges = ref([
  // Onboarding flow - left to right
  {
    id: 'e-a-header-a1',
    source: 'a-header',
    target: 'a1',
    animated: true,
    style: { stroke: '#667eea', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#667eea', width: 20, height: 20 }
  },
  {
    id: 'e-a1-a2',
    source: 'a1',
    target: 'a2',
    animated: true,
    style: { stroke: '#3b82f6', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#3b82f6', width: 20, height: 20 }
  },

  // Transition to Investment
  {
    id: 'e-a2-b-header',
    source: 'a2',
    target: 'b-header',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },

  // Investment process flow - left to right
  {
    id: 'e-b-header-b1',
    source: 'b-header',
    target: 'b1',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },
  {
    id: 'e-b1-b2',
    source: 'b1',
    target: 'b2',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },
  {
    id: 'e-b2-b3',
    source: 'b2',
    target: 'b3',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },

  // Transition to Redemption
  {
    id: 'e-b3-c-header',
    source: 'b3',
    target: 'c-header',
    animated: true,
    style: { stroke: '#8b5cf6', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#8b5cf6', width: 20, height: 20 }
  },

  // Redemption flow - left to right
  {
    id: 'e-c-header-c1',
    source: 'c-header',
    target: 'c1',
    animated: true,
    style: { stroke: '#8b5cf6', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#8b5cf6', width: 20, height: 20 }
  },
  {
    id: 'e-c1-c2',
    source: 'c1',
    target: 'c2',
    animated: true,
    style: { stroke: '#f59e0b', strokeWidth: 4 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#f59e0b', width: 22, height: 22 }
  },
  {
    id: 'e-c2-c3',
    source: 'c2',
    target: 'c3',
    animated: true,
    style: { stroke: '#8b5cf6', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#8b5cf6', width: 20, height: 20 }
  }
]);
</script>

<template>
  <div class="gift-city-workflow">
    <h2 class="workflow-title">GIFT City (IFSC) Investment Workflow</h2>
    <p class="workflow-intro">
      GIFT City is a "Non-Resident" zone within India, so your money leaves the domestic "Rupee" zone. You can fund it the traditional way (your own FCA/IBU account) or the simple way — straight from your domestic savings account via a fund's payment gateway.
    </p>

    <!-- Interactive Workflow Diagram with Vue Flow -->
    <div class="workflow-container" style="height: 600px; border: 2px solid #e5e7eb; border-radius: 16px; background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%); box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);">
      <VueFlow
        :nodes="nodes"
        :edges="edges"
        :fit-view-on-init="false"
        :min-zoom="0.6"
        :max-zoom="1.5"
        :default-viewport="{ zoom: 1, x: 0, y: 0 }"
      >
        <Background pattern-color="#d1d5db" :gap="20" size="1" />
        <Controls />
      </VueFlow>
    </div>

    <div class="workflow-info">
      <i class="pi pi-lightbulb"></i>
      <p><strong>Interactive Workflow:</strong> Use mouse wheel to zoom, drag to pan, and move nodes to customize the layout!</p>
    </div>

    <!-- Two Funding Routes -->
    <div class="funding-routes">
      <h3>💸 Two Ways to Fund Your GIFT City / Global Investment</h3>
      <p class="funding-intro">
        You do <strong>not</strong> always need to open your own Foreign Currency Account (FCA) with an IFSC Banking Unit (IBU). Many funds now let you remit straight from your existing domestic INR savings account into the fund's pooled USD account.
      </p>
      <div class="routes-grid">
        <!-- Route 1: Traditional FCA/IBU -->
        <div class="route-card traditional">
          <div class="route-head">
            <span class="route-tag">Route 1</span>
            <h4>🏦 Traditional — Your Own FCA / IBU</h4>
          </div>
          <ul class="route-points">
            <li>Open a personal Foreign Currency Account with an IBU (HDFC / ICICI / Kotak at GIFT City)</li>
            <li>Complete a separate IFSC KYC (5–7 business days)</li>
            <li>Remit INR → your USD FCA via LRS Form A2</li>
            <li>Place fund / ETF / UDR orders from your own USD balance</li>
          </ul>
          <div class="route-verdict best-for">
            ✅ Best for: Active traders & those wanting to <strong>hold USD indefinitely</strong> and redeploy across many products.
          </div>
        </div>

        <!-- Route 2: Payment Gateway -->
        <div class="route-card gateway">
          <div class="route-head">
            <span class="route-tag">Route 2</span>
            <h4>⚡ Simple — Fund's Payment Gateway (No FCA needed)</h4>
          </div>
          <ul class="route-points">
            <li>No personal FCA/IBU setup — pay from your <strong>domestic INR savings account</strong></li>
            <li>Fund's gateway auto-generates the <strong>Digital Form A2</strong> &amp; tracks TCS</li>
            <li>Money is wired via <strong>SWIFT</strong> into the fund's pooled <strong>USD Current Account</strong> at a domestic bank (e.g., Kotak Mahindra Bank)</li>
            <li>Units allotted directly inside the fund / app wrapper</li>
          </ul>
          <div class="route-verdict simple-for">
            ✅ Best for: Investors who want a <strong>one-click, account-free</strong> path into a specific global fund.
          </div>
        </div>
      </div>

      <!-- Real-world example -->
      <div class="route-example">
        <i class="pi pi-info-circle"></i>
        <div>
          <strong>Real-world example (Route 2):</strong> When investing in the <em>Marcellus Global Equities Fund</em> (a concentrated portfolio of durable, hard-to-disrupt businesses across the 🇺🇸 US, Europe, Taiwan &amp; Canada, picked through deep research with a strict risk &amp; fair-value lens), you can remit directly from your <strong>ICICI Bank savings account</strong> through Marcellus's <strong>"Glomo" payment gateway</strong> — no FCA/IBU required. The funds land in Marcellus's <strong>Kotak Mahindra Bank USD Current Account</strong> (identified by its SWIFT code).
          <p class="example-note">This gateway model is <strong>not unique to one fund</strong> — AMCs and platforms such as <strong>Vested Finance, INDmoney, Stockal, DSP, Mirae, Edelweiss</strong> and several GIFT City IFSC funds offer similar "pay-from-your-savings-account" rails that pool investor USD into a domestic bank's IFSC/USD current account.</p>
        </div>
      </div>
    </div>

    <!-- Key Highlights -->
    <div class="key-highlights">
      <h3>Key Highlights</h3>
      <div class="highlights-grid">
        <div class="highlight-card">
          <i class="pi pi-globe"></i>
          <h4>Access to Global Markets</h4>
          <p>Invest in 🇺🇸 stocks, global ETFs, and international mutual funds</p>
        </div>
        <div class="highlight-card">
          <i class="pi pi-dollar"></i>
          <h4>Account-Free Funding Option</h4>
          <p>Pay from your domestic savings account via a fund's payment gateway — no FCA/IBU needed</p>
        </div>
        <div class="highlight-card">
          <i class="pi pi-chart-line"></i>
          <h4>Better Forex Rates</h4>
          <p>Institutional rates better than retail forex</p>
        </div>
        <div class="highlight-card">
          <i class="pi pi-lock-open"></i>
          <h4>No Exit Limit</h4>
          <p>Unlimited repatriation back to India</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gift-city-workflow {
  max-width: 1200px;
  margin: 0 auto;
}

.workflow-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.workflow-intro {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.workflow-info {
  margin-top: 12px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-left: 3px solid #f59e0b;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #78350f;
  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.15);
}

.workflow-info i {
  color: #f59e0b;
  font-size: 1rem;
  flex-shrink: 0;
}

.workflow-info p {
  margin: 0;
  line-height: 1.4;
}

.workflow-info strong {
  color: #92400e;
  font-weight: 700;
}

/* Two Funding Routes */
.funding-routes {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 10px;
  border: 2px solid #e2e8f0;
}

.funding-routes h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.funding-intro {
  font-size: 0.82rem;
  color: #475569;
  margin: 0 0 14px 0;
  line-height: 1.5;
}

.funding-intro strong {
  color: #0f172a;
}

.routes-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 14px;
}

.route-card {
  background: white;
  border-radius: 10px;
  padding: 14px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.route-card.traditional {
  border-color: #93c5fd;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.route-card.gateway {
  border-color: #6ee7b7;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
}

.route-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.route-tag {
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.08);
  color: #334155;
}

.route-head h4 {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.route-points {
  list-style: none;
  padding: 0;
  margin: 0 0 12px 0;
  flex: 1;
}

.route-points li {
  position: relative;
  padding: 4px 0 4px 16px;
  font-size: 0.76rem;
  color: #374151;
  line-height: 1.45;
}

.route-points li::before {
  content: '▸';
  position: absolute;
  left: 0;
  color: #64748b;
  font-weight: 700;
}

.route-points li strong {
  color: #0f172a;
}

.route-verdict {
  font-size: 0.76rem;
  line-height: 1.45;
  padding: 8px 10px;
  border-radius: 6px;
}

.route-verdict strong {
  color: #0f172a;
}

.route-verdict.best-for {
  background: rgba(59, 130, 246, 0.12);
  color: #1e40af;
}

.route-verdict.simple-for {
  background: rgba(16, 185, 129, 0.12);
  color: #065f46;
}

.route-example {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
}

.route-example i {
  color: #f59e0b;
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.route-example > div {
  font-size: 0.78rem;
  color: #78350f;
  line-height: 1.55;
}

.route-example strong {
  color: #92400e;
}

.route-example em {
  font-style: italic;
  font-weight: 600;
  color: #92400e;
}

.example-note {
  margin: 8px 0 0 0;
  font-size: 0.74rem;
  color: #92400e;
  line-height: 1.5;
}

.key-highlights {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  border: 2px solid #bae6fd;
}

.key-highlights h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0 0 12px 0;
}

.highlights-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.highlight-card {
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 2px solid #bae6fd;
  transition: all 0.3s ease;
}

.highlight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(59, 130, 246, 0.15);
  border-color: #3b82f6;
}

.highlight-card i {
  font-size: 1.5rem;
  color: #3b82f6;
  margin-bottom: 8px;
}

.highlight-card h4 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 6px 0;
}

.highlight-card p {
  color: #6b7280;
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .workflow-title {
    font-size: 1.5rem;
  }

  .highlights-grid {
    grid-template-columns: 1fr;
  }

  .routes-grid {
    grid-template-columns: 1fr;
  }

  .workflow-container {
    height: 800px !important;
  }
}

@media (max-width: 1200px) {
  .workflow-container {
    height: 700px !important;
  }
}
</style>
